import { useEffect, useState } from "react";
import {
  FaTimes,
  FaSearchPlus,
  FaSearchMinus,
  FaUndo,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import type { HistoryItem } from "../../types/smileDesign/historyItem";

interface Props {
  history: HistoryItem[];
  leftIndex: number;
  rightIndex: number;
  onClose: () => void;
}

type Panel = "left" | "right";

export default function HistoryCompareViewer({
  history,
  leftIndex: initialLeft,
  rightIndex: initialRight,
  onClose,
}: Props) {
  const [leftIndex, setLeftIndex] = useState(initialLeft);
  const [rightIndex, setRightIndex] = useState(initialRight);

  const [zoomLeft, setZoomLeft] = useState(1);
  const [zoomRight, setZoomRight] = useState(1);
  const [posLeft, setPosLeft] = useState({ x: 0, y: 0 });
  const [posRight, setPosRight] = useState({ x: 0, y: 0 });

  const [dragging, setDragging] = useState<null | Panel>(null);
  const [start, setStart] = useState({ x: 0, y: 0 });

  const leftImage = history[leftIndex];
  const rightImage = history[rightIndex];

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const handleWheel = (e: React.WheelEvent<HTMLDivElement>, panel: Panel) => {
    e.preventDefault();
    const scale = 0.2;
    const delta = e.deltaY < 0 ? scale : -scale;

    if (panel === "left") {
      setZoomLeft((prev) => Math.min(Math.max(prev + delta, 1), 5));
    } else {
      setZoomRight((prev) => Math.min(Math.max(prev + delta, 1), 5));
    }
  };

  const beginDrag = (clientX: number, clientY: number, panel: Panel) => {
    setDragging(panel);
    setStart({ x: clientX, y: clientY });
  };

  const moveDrag = (clientX: number, clientY: number) => {
    if (!dragging) return;

    const dx = clientX - start.x;
    const dy = clientY - start.y;
    setStart({ x: clientX, y: clientY });

    if (dragging === "left") {
      setPosLeft((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
    } else {
      setPosRight((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
    }
  };

  const endDrag = () => setDragging(null);

  const resetView = (panel: Panel) => {
    if (panel === "left") {
      setZoomLeft(1);
      setPosLeft({ x: 0, y: 0 });
    } else {
      setZoomRight(1);
      setPosRight({ x: 0, y: 0 });
    }
  };

  const goPrevious = (panel: Panel) => {
    if (panel === "left") {
      if (leftIndex <= 0) return;
      setLeftIndex(leftIndex - 1);
      setZoomLeft(1);
      setPosLeft({ x: 0, y: 0 });
    } else {
      if (rightIndex <= 0) return;
      setRightIndex(rightIndex - 1);
      setZoomRight(1);
      setPosRight({ x: 0, y: 0 });
    }
  };

  const goNext = (panel: Panel) => {
    if (panel === "left") {
      if (leftIndex >= history.length - 1) return;
      setLeftIndex(leftIndex + 1);
      setZoomLeft(1);
      setPosLeft({ x: 0, y: 0 });
    } else {
      if (rightIndex >= history.length - 1) return;
      setRightIndex(rightIndex + 1);
      setZoomRight(1);
      setPosRight({ x: 0, y: 0 });
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }

      // Shift + arrows control the right panel; plain arrows control the left
      if (e.shiftKey) {
        if (e.key === "ArrowLeft") {
          if (rightIndex > 0) {
            setRightIndex(rightIndex - 1);
            setZoomRight(1);
            setPosRight({ x: 0, y: 0 });
          }
        } else if (e.key === "ArrowRight") {
          if (rightIndex < history.length - 1) {
            setRightIndex(rightIndex + 1);
            setZoomRight(1);
            setPosRight({ x: 0, y: 0 });
          }
        }
      } else {
        if (e.key === "ArrowLeft") {
          if (leftIndex > 0) {
            setLeftIndex(leftIndex - 1);
            setZoomLeft(1);
            setPosLeft({ x: 0, y: 0 });
          }
        } else if (e.key === "ArrowRight") {
          if (leftIndex < history.length - 1) {
            setLeftIndex(leftIndex + 1);
            setZoomLeft(1);
            setPosLeft({ x: 0, y: 0 });
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [leftIndex, rightIndex, history.length, onClose]);

  const renderPanel = (
    panel: Panel,
    label: string,
    index: number,
    item: HistoryItem | undefined,
    zoom: number,
    pos: { x: number; y: number },
    setZoom: (updater: (prev: number) => number) => void
  ) => (
    <div
      className={`w-full md:w-1/2 flex flex-col min-h-[45vh] md:min-h-0 ${
        panel === "left"
          ? "md:border-r border-white/10 bg-[#0d0d0d]"
          : "bg-[#101211] border-t md:border-t-0 border-white/10"
      }`}
    >
      <div className="h-12 sm:h-16 px-3 sm:px-4 border-b border-white/5 flex items-center justify-between gap-2 shrink-0">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <h3 className="text-base sm:text-lg font-semibold text-white shrink-0">{label}</h3>
          <p className="text-sm text-gray-400 shrink-0">
            {index + 1} / {history.length}
          </p>
          {item && (
            <p
              className="text-sm text-[#2fa9bf] truncate hidden sm:block"
              title={item.smileType || item.imageName}
            >
              {item.smileType || item.imageName}
            </p>
          )}
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <button
            type="button"
            onClick={() => setZoom((prev) => Math.min(prev + 0.2, 5))}
            className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-white/5 hover:bg-[#2fa9bf] transition-all duration-300 flex items-center justify-center text-white"
            aria-label={`Zoom in ${label}`}
          >
            <FaSearchPlus />
          </button>
          <button
            type="button"
            onClick={() => setZoom((prev) => Math.max(prev - 0.2, 1))}
            className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-white/5 hover:bg-[#2fa9bf] transition-all duration-300 flex items-center justify-center text-white"
            aria-label={`Zoom out ${label}`}
          >
            <FaSearchMinus />
          </button>
          <button
            type="button"
            onClick={() => resetView(panel)}
            className="h-9 w-9 sm:h-10 sm:w-10 rounded-lg bg-white/5 hover:bg-[#2fa9bf] transition-all duration-300 flex items-center justify-center text-white"
            aria-label={`Reset ${label}`}
          >
            <FaUndo />
          </button>
        </div>
      </div>

      <div
        className="relative flex-1 flex items-center justify-center overflow-hidden cursor-grab touch-none min-h-[220px]"
        onWheel={(e) => handleWheel(e, panel)}
        onMouseDown={(e) => beginDrag(e.clientX, e.clientY, panel)}
        onTouchStart={(e) => {
          if (e.touches.length === 0) return;
          beginDrag(e.touches[0].clientX, e.touches[0].clientY, panel);
        }}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            goPrevious(panel);
          }}
          disabled={index === 0}
          className={`absolute left-2 sm:left-5 z-20 h-10 w-10 sm:h-14 sm:w-14 rounded-full flex items-center justify-center transition
            ${
              index === 0
                ? "bg-white/5 text-gray-600 cursor-not-allowed opacity-30"
                : "bg-black/60 hover:bg-[#2fa9bf] text-white backdrop-blur-sm cursor-pointer"
            }`}
          aria-label={`Previous ${label} image`}
        >
          <FaChevronLeft size={18} />
        </button>

        {item && (
          <img
            src={item.image}
            alt={label}
            draggable={false}
            className="object-contain max-w-full max-h-full select-none transition-transform duration-75"
            style={{
              transform: `translate(${pos.x}px, ${pos.y}px) scale(${zoom})`,
              cursor: dragging === panel ? "grabbing" : "grab",
            }}
          />
        )}

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            goNext(panel);
          }}
          disabled={index === history.length - 1}
          className={`absolute right-2 sm:right-5 z-20 h-10 w-10 sm:h-14 sm:w-14 rounded-full flex items-center justify-center transition
            ${
              index === history.length - 1
                ? "bg-white/5 text-gray-600 cursor-not-allowed opacity-30"
                : "bg-black/60 hover:bg-[#2fa9bf] text-white backdrop-blur-sm cursor-pointer"
            }`}
          aria-label={`Next ${label} image`}
        >
          <FaChevronRight size={18} />
        </button>
      </div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[999] bg-[#080808]/95 backdrop-blur-xl flex flex-col"
      onMouseMove={(e) => moveDrag(e.clientX, e.clientY)}
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
      onTouchMove={(e) => {
        if (!dragging || e.touches.length === 0) return;
        moveDrag(e.touches[0].clientX, e.touches[0].clientY);
      }}
      onTouchEnd={endDrag}
    >
      <div className="h-14 sm:h-20 border-b border-white/10 bg-[#101211]/90 px-3 sm:px-6 flex items-center justify-between shrink-0">
        <div className="min-w-0">
          <h2 className="text-lg sm:text-2xl font-bold text-white truncate">
            Compare After Designs
          </h2>
          <p className="text-sm text-gray-400 mt-0.5 hidden sm:block">
            Prev/next on each side · Shift+arrows for right panel · Esc to close
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-red-500/10 hover:bg-red-500 text-red-400 hover:text-white transition-all duration-300 flex items-center justify-center shrink-0"
          aria-label="Close compare fullscreen"
        >
          <FaTimes />
        </button>
      </div>

      <div className="flex flex-col md:flex-row flex-1 min-h-0 overflow-y-auto md:overflow-hidden">
        {renderPanel(
          "left",
          "After A",
          leftIndex,
          leftImage,
          zoomLeft,
          posLeft,
          setZoomLeft
        )}
        {renderPanel(
          "right",
          "After B",
          rightIndex,
          rightImage,
          zoomRight,
          posRight,
          setZoomRight
        )}
      </div>
    </div>
  );
}
